import * as React from 'react';
import { TextFieldProps, useGetManyReference, useGetOne, useRecordContext } from 'react-admin';
import { Button } from '@mui/material';
import TransformIcon from '@mui/icons-material/Transform';
import { Contract } from '../types';
import ConvertPreneedDialog from './ConvertPreneedDialog';
import PreplanConvertDialog from './PreplanConvertDialog';
import { isPreplanOnly } from '../funerals/preneedMode';

export type ConvertPreNeedButtonProps = TextFieldProps & {
  contract?: Contract;
  hasAtNeed?: boolean;
};

const ConvertPreNeedButton = ({ contract, hasAtNeed }: ConvertPreNeedButtonProps) => {
  const record = useRecordContext<Contract>();
  const targetContract = contract || record;
  const [open, setOpen] = React.useState(false);

  const shouldFetchContracts = hasAtNeed === undefined && !!targetContract?.funeral_id;
  const { data: funeralContracts } = useGetManyReference(
    'contracts',
    { target: 'funeral_id', id: targetContract?.funeral_id },
    { enabled: shouldFetchContracts }
  );

  // A pre-plan only case has no TruStage policy to fulfil (T-172), so it
  // converts to a plain at-need contract instead.
  const { data: funeral } = useGetOne(
    'funerals',
    {
      id: targetContract?.funeral_id,
      meta: { select: 'id, region, preneed_mode, has_atneed, contracts_signed_at' },
    },
    { enabled: !!targetContract?.funeral_id },
  );
  const preplan = isPreplanOnly(funeral);

  const computedHasAtNeed = hasAtNeed ?? (funeralContracts || []).some((c) => !c.is_preneed);

  const shouldShow =
    !!targetContract &&
    targetContract.is_preneed &&
    targetContract.fulfillment_status !== 'fulfilled' &&
    !computedHasAtNeed;

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<TransformIcon />}
        onClick={() => setOpen(true)}
      >
        {preplan ? 'Convert to at-need' : 'Fruition'}
      </Button>
      {targetContract && preplan && (
        <PreplanConvertDialog
          open={open}
          onClose={() => setOpen(false)}
          contract={targetContract}
          region={funeral?.region}
        />
      )}
      {targetContract && !preplan && (
        <ConvertPreneedDialog
          open={open}
          onClose={() => setOpen(false)}
          contract={targetContract}
        />
      )}
    </>
  );
};

export default ConvertPreNeedButton;

